'use client';

import React, { useCallback, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { HandThumbUpIcon, HandThumbDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolid,
  HandThumbDownIcon as HandThumbDownSolid,
} from '@heroicons/react/24/solid';
import type { InteractionFeedback } from '@/types';

export type FeedbackRating = 'positive' | 'negative';

interface MessageFeedbackControlsProps {
  messageId: string;
  onFeedback: (messageId: string, feedback: InteractionFeedback) => Promise<void> | void;
  /** Customize the payload sent on the initial thumbs click. Defaults to `{ rating, userModified: false }`. */
  buildRatingFeedback?: (rating: FeedbackRating) => InteractionFeedback;
  /** Called after a rating is recorded (e.g., agent-specific feedback tracking). */
  onRatingGiven?: (rating: FeedbackRating) => void | Promise<void>;
  /** Force the "Thanks!" confirmation even before a thumbs rating (e.g., agent feedback recorded elsewhere). */
  showThanks?: boolean;
  /** Hint shown after the optional note is sent. */
  noteSentHint?: string;
  /** Extra inline controls rendered next to the thumbs (e.g., agent "modified" flow). */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Shared thumbs up/down + optional note feedback controls.
 * Used by both article mode (ArticleMessage) and bubble mode (LiquidGlassMessage)
 * so both render paths expose an identical feedback UX.
 */
export function MessageFeedbackControls({
  messageId,
  onFeedback,
  buildRatingFeedback,
  onRatingGiven,
  showThanks = false,
  noteSentHint = 'Applied to future drafts when confidence passes.',
  children,
  className,
}: MessageFeedbackControlsProps) {
  const [feedbackGiven, setFeedbackGiven] = useState<FeedbackRating | null>(null);
  const [feedbackDetailsOpen, setFeedbackDetailsOpen] = useState(false);
  const [feedbackDetails, setFeedbackDetails] = useState('');
  const [feedbackDetailsSubmitted, setFeedbackDetailsSubmitted] = useState(false);
  const [isSubmittingDetails, setIsSubmittingDetails] = useState(false);

  const handleRatingClick = useCallback(
    async (rating: FeedbackRating) => {
      if (feedbackGiven) return;
      setFeedbackGiven(rating);
      try {
        await onFeedback(
          messageId,
          buildRatingFeedback ? buildRatingFeedback(rating) : { rating, userModified: false },
        );
        setFeedbackDetailsOpen(true);
      } catch (error) {
        console.error('onFeedback failed', error);
      }
      if (onRatingGiven) {
        await onRatingGiven(rating);
      }
    },
    [buildRatingFeedback, feedbackGiven, messageId, onFeedback, onRatingGiven],
  );

  const handleDetailsSubmit = useCallback(async () => {
    const details = feedbackDetails.trim();
    if (!feedbackGiven || !details || feedbackDetailsSubmitted || isSubmittingDetails) return;
    setIsSubmittingDetails(true);
    try {
      await onFeedback(messageId, {
        rating: feedbackGiven,
        details,
        userModified: false,
      });
      setFeedbackDetailsSubmitted(true);
    } catch (error) {
      console.error('onFeedback details submission failed', error);
    } finally {
      setIsSubmittingDetails(false);
    }
  }, [feedbackDetails, feedbackDetailsSubmitted, feedbackGiven, isSubmittingDetails, messageId, onFeedback]);

  return (
    <div className={cn('opacity-80 hover:opacity-100 transition-opacity', className)}>
      <div className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground mr-1">Helpful?</span>
        {/* Positive */}
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => { void handleRatingClick('positive'); }}
          disabled={!!feedbackGiven}
          className={cn(
            feedbackGiven === 'positive' ? 'text-success bg-success/10' : 'text-muted-foreground hover:text-success'
          )}
          aria-label="Good response"
          title="Good response"
        >
          {feedbackGiven === 'positive' ? (
            <HandThumbUpSolid className="h-4 w-4" />
          ) : (
            <HandThumbUpIcon className="h-4 w-4" />
          )}
        </Button>
        {/* Negative */}
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => { void handleRatingClick('negative'); }}
          disabled={!!feedbackGiven}
          className={cn(
            feedbackGiven === 'negative' ? 'text-destructive bg-destructive/10' : 'text-muted-foreground hover:text-destructive'
          )}
          aria-label="Poor response"
          title="Poor response"
        >
          {feedbackGiven === 'negative' ? (
            <HandThumbDownSolid className="h-4 w-4" />
          ) : (
            <HandThumbDownIcon className="h-4 w-4" />
          )}
        </Button>
        {children}
        {(feedbackGiven || showThanks) && (
          <span className="text-xs text-muted-foreground ml-1 flex items-center gap-1">
            <CheckIcon className="h-3 w-3" />
            Thanks!
          </span>
        )}
        {feedbackGiven && (
          <button
            type="button"
            onClick={() => setFeedbackDetailsOpen((prev) => !prev)}
            className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground ml-1"
          >
            {feedbackDetailsOpen ? 'Hide note' : 'Add note'}
          </button>
        )}
      </div>
      {feedbackDetailsOpen && feedbackGiven && (
        <div className="mt-2 flex flex-col gap-2 rounded-md border border-border/50 bg-background/60 p-2">
          <textarea
            value={feedbackDetails}
            onChange={(event) => setFeedbackDetails(event.target.value)}
            placeholder="Optional: what should be improved next time?"
            rows={2}
            className="w-full rounded-md border border-border/60 bg-background/70 px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { void handleDetailsSubmit(); }}
              disabled={!feedbackDetails.trim() || feedbackDetailsSubmitted || isSubmittingDetails}
            >
              {feedbackDetailsSubmitted ? 'Note sent' : isSubmittingDetails ? 'Sending...' : 'Send note'}
            </Button>
            {feedbackDetailsSubmitted && (
              <span className="text-[11px] text-muted-foreground">{noteSentHint}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageFeedbackControls;
